import { Search as SearchIcon } from '@boxicons/react';
import Search from '../../components/input/search/Search';
import { useCallback, useEffect, useRef, useState, type SubmitEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useWindowSize } from 'usehooks-ts';
import ProfileButtons from '../../components/shared/ProfileButtons';
import { useShortcut } from 'react-keybind';

export default function Header() {
    const windowSize = useWindowSize();
    const navigate = useNavigate();
    const [text, setText] = useState('');
    const ref = useRef<HTMLInputElement>(null);

    const { registerShortcut, unregisterShortcut } = useShortcut()!;
    useEffect(() => {
        const keybinds = ['ctrl+k', 'cmd+k'];
        registerShortcut(
            () => {
                if (window.location.pathname !== '/search') {
                    if (ref.current) {
                        ref.current.focus();
                        ref.current.select();
                    }
                }
            },
            keybinds,
            'Search',
            'Search levels',
        );

        return () => {
            unregisterShortcut(keybinds);
        };
    }, [registerShortcut, unregisterShortcut]);

    const goToSearch = useCallback(() => {
        if (window.location.pathname !== '/search') {
            void navigate('/search');
        }
    }, [navigate]);

    const onSearch = useCallback(
        (e: SubmitEvent) => {
            e.preventDefault();
            if (text === '') {
                goToSearch();
                return;
            }

            sessionStorage.setItem('level-filters', JSON.stringify({ name: text }));

            if (window.location.pathname === '/search') {
                window.location.reload();
            } else {
                void navigate(`/search?name=${encodeURIComponent(text)}`);
            }
        },
        [goToSearch, navigate, text],
    );

    return (
        <header className='bg-theme-header text-theme-header-text shadow-lg outline-b outline-theme-outline sticky top-0 left-0 right-0 z-20'>
            <nav className='pe-6 md:pe-10 relative flex items-center justify-between'>
                <Link to='/' className='z-10 min-w-0 shrink'>
                    <img src='/banner.webp' width={windowSize.width >= 766 ? 300 : 220} className='max-w-full' />
                </Link>
                <ProfileButtons />
                <div className='absolute left-0 right-0 flex justify-center max-lg:hidden'>
                    <form onSubmit={onSearch} className='w-sm xl:w-lg relative'>
                        <Search
                            ref={ref}
                            value={text}
                            onChange={(e) => setText(e.target.value.trimStart().slice(0, 22))}
                            onClear={() => {
                                setText('');
                                ref.current?.focus();
                            }}
                            leading={
                                <button
                                    type='button'
                                    onClick={goToSearch}
                                    title='Go to search page'
                                    className='cursor-pointer text-theme-400 hover:text-inherit transition-colors'
                                >
                                    <SearchIcon />
                                </button>
                            }
                            autoFocus
                            placeholder='Search levels and users'
                        />
                        <p
                            className={
                                'absolute pointer-events-none right-4 top-1/2 -translate-y-1/2 text-theme-400 text-xs outline rounded-md px-1 ' +
                                (text ? 'hidden' : '')
                            }
                        >
                            Ctrl + K
                        </p>
                    </form>
                </div>
            </nav>
        </header>
    );
}
